import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Grid,
    GridItem,
    VStack,
    Text,
    HStack,
    useDisclosure, useToast, Button, useBreakpointValue,
} from '@chakra-ui/react';

import {useDispatch, useSelector} from "react-redux";
import {FiChevronLeft, FiChevronRight} from "react-icons/fi";

import {useTranslation} from "react-i18next";
import {
    changeNameOfAudiobook,
    changeNameOfDocumentGeneration, changeNameOfDocumentGenerationNative,
    setCurrentPageGridAudiobooks
} from "../../redux/reducers";
import {useAuth} from "../../redux/AuthProvider";
import {useServerIP} from "../../redux/ServerIPContext";
import {renameMultidoc} from "../../utils/multi-doc-helpers";
import {MySpinner} from "../../components/Reuseable/MySpinner";
import VerticalMenu from "../../components/Reuseable/VerticalMenu";
import DeleteScreenGeneration from "../../components/Modals/DeleteModal/DeleteScreenGeneration";

const DocumentGridAudiobooks = ({ navigate }) => {
    const { isOpen: isOpenDeleteGeneration, onOpen: onOpenDeleteGeneration, onClose: onCloseDeleteGeneration } = useDisclosure();
    const [selectedFileID, setSelectedFileID] = useState(null);
    const [editableId, setEditableId] = useState(null);
    const [newNames, setNewNames] = useState({});
    const inputRef = useRef(null);
    const toast = useToast();
    const dispatch = useDispatch();
    const auth = useAuth();
    const audiobooks = useSelector((state) => state.documents.savedGenerationsNative);
    const [thumbnails, setThumbnails] = useState({});
    const breakpoint = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' });
    const isMobile = breakpoint === 'base' || breakpoint === 'sm';
    const {t} = useTranslation();
    const { serverIPs, setServerIPs, isDev } = useServerIP();

    useEffect(() => {
        function handleClickOutside(event) {
            if (inputRef.current && !inputRef.current.contains(event.target)) {
                setEditableId(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handlePostRequest = async (newName, userID, fileID) => {
        await renameMultidoc(newName, userID, fileID, serverIPs.SERVER_IP_MULTIDOC).then((response) => {
            if (response === null) {
                toast({
                    title: t("Oops, something went wrong. Please try again."),
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                })
            } else if (response.status === 401) {
                toast({
                    title: t("401 error. Please try again."),
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                })
            } else if (response.status === 200) {
                dispatch(changeNameOfDocumentGenerationNative({
                    newName: newName,
                    documentID: fileID,
                }));
                toast({
                    title: t("Successfully renamed."),
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                })
            }
        });

        // await renameAudiobook(newName, userID, fileID, serverIPs.SERVER_IP).then((response) => {
        //     if (response === null) {
        //         toast({
        //             title: t("Oops, something went wrong. Please try again."),
        //             status: "error",
        //             duration: 5000,
        //             isClosable: true,
        //         })
        //     } else if (response.status === 400) {
        //         toast({
        //             title: t("400 error. Please try again."),
        //             status: "error",
        //             duration: 5000,
        //             isClosable: true,
        //         })
        //     } else if (response.status === 200) {
        //         dispatch(changeNameOfAudiobook({
        //             newName: newName,
        //             documentID: fileID,
        //         }));
        //         toast({
        //             title: t("Successfully renamed."),
        //             status: "success",
        //             duration: 5000,
        //             isClosable: true,
        //         })
        //     }
        // });
    };

    const documentsPerPage = 15; // Set the number of documents per page
    const currentPageGrid = useSelector((state) => state.settings.currentPageGridAudiobooks);
    const totalPages = audiobooks !== null ? Math.ceil(audiobooks.length / documentsPerPage) : 0;

    // Function to calculate the slice of documents to display
    const indexOfLastDoc = currentPageGrid * documentsPerPage;
    const indexOfFirstDoc = indexOfLastDoc - documentsPerPage;
    const currentDocuments = audiobooks.slice(indexOfFirstDoc, indexOfLastDoc);

    // Function to change page
    const paginate = (pageNumber) => dispatch(setCurrentPageGridAudiobooks(pageNumber));

    // Function to go to the next page
    const nextPage = () => dispatch(setCurrentPageGridAudiobooks(Math.min(currentPageGrid + 1, totalPages)));

    // Function to go to the previous page
    const prevPage = () => dispatch(setCurrentPageGridAudiobooks(Math.max(currentPageGrid - 1, 1)));

    useEffect(() => {
        currentDocuments.forEach(async (document) => {
            if (!thumbnails[document.id]) {
                setThumbnails((prevThumbnails) => ({
                    ...prevThumbnails,
                    [document.id]: { loading: true },
                }));

                try {
                    // Get up to 3 image URLs
                    const imageUrls = document.urls?.slice(0, 3).map(urlObj => urlObj.url) || [];
                    // console.log("THUMBNAIL DATA URLs", imageUrls);
                    if (imageUrls.length > 0) {
                        setThumbnails((prevThumbnails) => ({
                            ...prevThumbnails,
                            [document.id]: { loading: false, dataUrls: imageUrls },
                        }));
                    } else {
                        // No URLs available
                        setThumbnails((prevThumbnails) => ({
                            ...prevThumbnails,
                            [document.id]: { loading: false, error: true },
                        }));
                    }
                } catch (error) {
                    console.error('Error creating thumbnail Missing PDF Exception:', error);
                    setThumbnails((prevThumbnails) => ({
                        ...prevThumbnails,
                        [document.id]: { loading: false, error: true },
                    }));
                }
            }
        });
    }, [currentDocuments]);

    return (
        <VStack>
            {!isMobile ?
                <HStack justify="space-between" align="center" mt={'-4rem'} mb={'2rem'} width={'100%'} >
                    <Text fontSize={"15px"}>
                        {t("Showing")} {indexOfFirstDoc + 1}-{indexOfLastDoc > audiobooks.length ? audiobooks.length : indexOfLastDoc} of {audiobooks.length}
                    </Text>

                    <HStack spacing={3}>
                        <Button onClick={prevPage} isDisabled={currentPageGrid === 1} padding={0} size={'sm'} borderColor={'lightgray'} borderWidth={0.25}
                                shadow={`1.5px 1.8px 0px lightgray`}>
                            <FiChevronLeft size={'1.23rem'}/>
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <Button
                                onClick={() => paginate(i + 1)}
                                key={i}
                                colorScheme={currentPageGrid === i + 1 ? 'orange' : 'gray'}
                                padding={0}
                                size={'sm'}
                                borderColor={'lightgray'} borderWidth={0.25}
                                shadow={`1.5px 1.8px 0px lightgray`}
                            >
                                {i + 1}
                            </Button>
                        ))}
                        <Button onClick={nextPage} isDisabled={currentPageGrid === totalPages} padding={0} size={'sm'} borderColor={'lightgray'} borderWidth={0.25}
                                shadow={`1.5px 1.8px 0px lightgray`}>
                            <FiChevronRight size={'1.23rem'}/>
                        </Button>
                    </HStack>
                </HStack> :
                <VStack justify="space-between" align="center" mt={'-4rem'} mb={'2rem'} width={'100%'} spacing={5}>
                    <Text fontSize={"15px"}>
                        {t("Showing")} {indexOfFirstDoc + 1}-{indexOfLastDoc > audiobooks.length ? audiobooks.length : indexOfLastDoc} of {audiobooks.length}
                    </Text>

                    <HStack spacing={3}>
                        <Button onClick={prevPage} isDisabled={currentPageGrid === 1} padding={0} size={'sm'} borderColor={'lightgray'} borderWidth={0.25}
                                shadow={`1.5px 1.8px 0px lightgray`}>
                            <FiChevronLeft size={'1.23rem'}/>
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <Button
                                onClick={() => paginate(i + 1)}
                                key={i}
                                colorScheme={currentPageGrid === i + 1 ? 'orange' : 'gray'}
                                padding={0}
                                size={'sm'}
                                borderColor={'lightgray'} borderWidth={0.25}
                                shadow={`1.5px 1.8px 0px lightgray`}
                            >
                                {i + 1}
                            </Button>
                        ))}
                        <Button onClick={nextPage} isDisabled={currentPageGrid === totalPages} padding={0} size={'sm'} borderColor={'lightgray'} borderWidth={0.25}
                                shadow={`1.5px 1.8px 0px lightgray`}>
                            <FiChevronRight size={'1.23rem'}/>
                        </Button>
                    </HStack>
                </VStack>}
            <Grid templateColumns={{ base: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)", xl: "repeat(5, 1fr)" }}
                  gap={'1.5rem'}
                  pb={'2rem'}>
                {currentDocuments.map((document) => {
                    const imageCount = Math.min(thumbnails[document.id]?.dataUrls?.length || 0, 3);

                    return (
                        <GridItem key={document.id}>
                            <Box>
                                <VStack spacing={'0rem'} cursor="pointer"
                                        borderWidth={'0px'}
                                        borderRadius={'8px'}
                                        overflow={'hidden'}
                                        bg={'white'}
                                        boxShadow={'0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)'}
                                        transition={'all 0.2s'}
                                        _hover={{
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
                                            transform: 'translateY(-2px)'
                                        }}>
                                        <Box
                                            display={'flex'}
                                            justifyContent={'flex-start'}
                                            alignItems={'flex-start'}
                                            height={'18rem'}
                                            width={'15rem'}
                                            bg={'gray.50'}
                                            p={'1.5rem'}
                                            onClick={() => {
                                                navigate(`/images/${document.id}`)
                                            }}
                                        >
                                            {thumbnails[document.id]?.loading || !thumbnails[document.id] ?
                                                <Box width={'100%'} height={'100%'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                                                    <MySpinner/>
                                                </Box> :
                                                <Box
                                                    position="relative"
                                                    height="100%"
                                                    width="100%"
                                                    style={{
                                                        paddingBottom: `${(imageCount - 1) * 12}px`,
                                                        paddingRight: `${(imageCount - 1) * 12}px`
                                                    }}
                                                >
                                                    {thumbnails[document.id]?.dataUrls?.slice(0, 3).reverse().map((url, reverseIndex) => {
                                                        const totalImages = Math.min(thumbnails[document.id].dataUrls.length, 3);
                                                        const index = totalImages - 1 - reverseIndex; // Get original index
                                                        const offsetX = index * 12; // Increased horizontal offset
                                                        const offsetY = index * 12; // Increased vertical offset
                                                        const zIndex = index + 1;

                                                        return (
                                                            <Box
                                                                key={index}
                                                                position="absolute"
                                                                top="0"
                                                                left="0"
                                                                zIndex={zIndex}
                                                                transform={`translate(${offsetX}px, ${offsetY}px)`}
                                                                boxShadow="0 4px 12px rgba(0,0,0,0.25)"
                                                                borderWidth="3px"
                                                                borderColor="white"
                                                                bg="white"
                                                                borderRadius="4px"
                                                                overflow="hidden"
                                                                transition="transform 0.2s"
                                                                _hover={{
                                                                    transform: `translate(${offsetX}px, ${offsetY}px) scale(1.02)`
                                                                }}
                                                            >
                                                                <img
                                                                    src={url}
                                                                    style={{
                                                                        height: `calc(15rem - ${(imageCount - 1) * 12}px)`,
                                                                        width: `calc(12rem - ${(imageCount - 1) * 12}px)`,
                                                                        objectFit: 'cover',
                                                                        display: 'block'
                                                                    }}
                                                                    alt={`${document.name} - ${index + 1}`}
                                                                />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            }
                                        </Box>
                                    <HStack width={'15rem'} spacing={0}
                                            justifyContent={auth.isLoggedIn ? 'flex-start' : 'center'}
                                            borderTopWidth={'1px'} borderColor={'gray.200'} py={'0.5rem'}
                                            pr={'0.5rem'} pl={auth.isLoggedIn ? '0rem' : '0.5rem'}
                                            bg={'white'}>
                                        {auth.isLoggedIn ?
                                            <VerticalMenu
                                                menuOptions={[
                                                    {
                                                        label: t("Rename Image Workspace"),
                                                        onClick: () => {
                                                            setEditableId(document.id);
                                                            setNewNames({ ...newNames, [document.id]: document.name });
                                                        },
                                                    },
                                                    {
                                                        label: t("Delete Image Workspace"),
                                                        onClick: () => {
                                                            setSelectedFileID(document.id);
                                                            onOpenDeleteGeneration();
                                                        },
                                                    },
                                                ]}
                                            /> : null}
                                        {editableId === document.id ? (
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                value={newNames[document.id] || ''}
                                                onChange={(e) => setNewNames({ ...newNames, [document.id]: e.target.value })}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handlePostRequest(newNames[document.id], localStorage.getItem("userID"), document.id).then(r => {

                                                        });
                                                        setEditableId(null);
                                                    }
                                                }}
                                                style={{ fontSize: 'small', width: '100%', cursor: 'text', border: '1px solid gray',
                                                    padding: '0.25rem', borderRadius: '0.25rem'}}
                                            />
                                        ) : (
                                            <Box onClick={() => {
                                                navigate(`/images/${document.id}`)
                                            }}>
                                                <Text fontSize={'sm'} fontWeight={'normal'} noOfLines={2}>
                                                    {document.name}
                                                </Text>
                                            </Box>
                                        )}
                                    </HStack>
                                </VStack>
                            </Box>
                        </GridItem>
                    );
                })}
                {/*{selectedFileID !== null ?*/}
                {/*    <DeleteScreen1 onCloseDelete1={onCloseDelete1} isOpenDelete1={isOpenDelete1} userID={localStorage.getItem("userID")}*/}
                {/*                   fileID={selectedFileID}/> : null}*/}
                {selectedFileID !== null ?
                    <DeleteScreenGeneration onCloseDeleteGeneration={onCloseDeleteGeneration} isOpenDeleteGeneration={isOpenDeleteGeneration}
                                            userID={localStorage.getItem("userID")}
                                            generationID={selectedFileID}/> : null}
            </Grid>
        </VStack>
    );
};

export default DocumentGridAudiobooks;
